import { Injectable, OnDestroy } from '@angular/core';
import {
    Router,
    ActivatedRoute,
    Event,
    NavigationEnd,
    NavigationStart,
    RouteConfigLoadStart,
    GuardsCheckEnd,
    NavigationError,
    NavigationCancel,
    RouteConfigLoadEnd,
    GuardsCheckStart
} from '@angular/router';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, ReplaySubject, Subject, takeUntil, tap } from 'rxjs';

import { RouterState } from '../entities';
import { getRouteData } from '../utils';

function isNavigationStart(event: Event): boolean {
    return event instanceof NavigationStart || event instanceof RouteConfigLoadStart || event instanceof GuardsCheckEnd;
}

function isNavigationEnd(event: Event): boolean {
    return (
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        event instanceof NavigationCancel ||
        event instanceof RouteConfigLoadEnd ||
        event instanceof GuardsCheckStart
    );
}

@Injectable({
    providedIn: 'root'
})
export class RouterStoreService implements OnDestroy {
    private state?: RouterState;
    private readonly stateSubject$ = new ReplaySubject<RouterState>(1);
    private readonly busySubject$ = new BehaviorSubject<boolean>(false);
    private readonly destroy$ = new Subject<void>();

    readonly state$ = this.stateSubject$.asObservable();
    readonly busy$ = this.busySubject$.asObservable().pipe(debounceTime(200), distinctUntilChanged());

    constructor(router: Router, route: ActivatedRoute) {
        router.events
            .pipe(
                tap({
                    next: (event) => {
                        isNavigationStart(event) && this.busySubject$.next(true);
                        isNavigationEnd(event) && this.busySubject$.next(false);
                    },
                    error: () => this.busySubject$.next(false)
                }),
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map((event) => ({ ...getRouteData(route), url: event.urlAfterRedirects, path: event.urlAfterRedirects?.split('?')?.[0] })),
                distinctUntilChanged((prev, curr) => prev.url === curr.url),
                takeUntil(this.destroy$)
            )
            .subscribe((state) => this.updateState(state));
    }

    getValue(): RouterState | undefined {
        return this.state;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    private updateState(state: RouterState): void {
        this.state = { ...state };
        this.stateSubject$.next(this.state);
    }
}
